import { Asset, TransactionType } from "@prisma/client";
import { z } from "zod";

const NumericSchema = z.coerce.number();

export const CsvTransactionSchema = z.object({
  Date: z.coerce.date(),
  Token: z.string(),
  Type: z.enum(["buy", "sell"]),
  "Price (USD)": z.number().nonnegative(),
  Amount: z.number().nonnegative(),
  "Total value (USD)": z.number().nonnegative(),
  Fee: z.number().nullish(),
  "Fee Currency": z.string().nullish(),
  Notes: z.string().nullish(),
});
export type CsvTransaction = z.infer<typeof CsvTransactionSchema>;

export const AssetTransactionFeeSchema = z
  .object({
    cost: NumericSchema.optional().default(0),
    assetId: z.string().cuid(),
    asset: z
      .object({
        symbol: z.string(),
      })
      .optional(),
  })
  .transform(({ asset, ...rest }) => {
    return {
      ...rest,
      symbol: asset?.symbol ?? "",
    };
  });

export const AssetTransactionSchema = z.object({
  id: z.string().cuid().optional(),
  assetId: z.string(),
  quantity: NumericSchema.optional().default(0),
  fee: AssetTransactionFeeSchema.nullish(),
  amount: NumericSchema.optional().default(0),
  weight: NumericSchema.optional().default(0),
  price: NumericSchema.optional().default(0),
  transactionType: z.nativeEnum(TransactionType),
  portfolioId: z.union([z.literal(""), z.string().cuid()]),
  userId: z.string(),
  thirdPartyIds: z.record(z.string()).optional(),
  createdAt: z.union([z.coerce.date(), z.date()]),
  note: z.string().nullish().default(""),
  meta: z.record(z.unknown()).nullish(),
});

export const AssetTransactionDraftSchema = AssetTransactionSchema.extend({
  createdAt: z.union([z.coerce.date(), z.date()]),
  fee: AssetTransactionFeeSchema.nullish(),
});

export type AssetTransaction = z.infer<typeof AssetTransactionSchema>;
export type AssetTransactionDraft = z.infer<typeof AssetTransactionDraftSchema>;

const AssetTransactionListSchema = z.array(AssetTransactionSchema);
export function parseAssetTransactions(data: unknown) {
  const result = AssetTransactionListSchema.safeParse(data);

  return result.success ? result.data : [];
}

const AssetTransactionDraftListSchema = z.array(AssetTransactionDraftSchema);
export function parseAssetTransactionDrafts(data: unknown) {
  const result = AssetTransactionDraftListSchema.safeParse(data);

  return result.success ? result.data : [];
}

export function parseCsvTransactionToAssetTransaction(
  asset: Asset,
  data: CsvTransaction,
): AssetTransactionDraft {
  const result = CsvTransactionSchema.safeParse(data);

  if (!result.success) {
    throw new Error(result.error.errors.join(", "));
  }

  return {
    assetId: asset.id,
    quantity: data.Amount,
    amount: data["Total value (USD)"],
    transactionType:
      data.Type === "buy" ? TransactionType.BUY : TransactionType.SELL,
    createdAt: data.Date,
    note: data.Notes ?? null,
    userId: "",
    portfolioId: "",
    price: data["Price (USD)"],
    weight: 0,
    fee: {
      cost: data.Fee ?? 0,
      // TODO: This is a hack, we should find the asset based on FeeCurrency
      assetId: asset.id,
      symbol: asset.symbol,
    },
    thirdPartyIds: asset.thirdPartyIds as Record<string, string>,
  } satisfies AssetTransactionDraft;
}
